import classNames from 'classnames';
import React, { ChangeEvent, useEffect, useState } from 'react';

import Button from '~components/Button/Button';
import ReactInputDateMask from '~components/ReactInputDateMask/ReactInputDateMask';
import MarketoForm from '~globals/Marketo/marketoForm';
import { getAdvertiseId, isValidEmail } from '~globals/common';
import { fireTrackEvent } from '~globals/tracking';
import { useAppDispatch, useAppSelector } from '~hooks/useRedux';
import useWindowSize from '~hooks/useWindowSize';
import errorIcon from '~images/common/error-icon.svg';
import { updateLead } from '~redux/Actions/GlobalActions';

import Spinner from '../../Spinner/Spinner';

import { createLead } from './query';

type Props = {
    uid: string;
    mktoFormId?: number;
    isMobileSideBySide?: boolean;
    isStacked?: boolean;
    className?: string;
    buttonText?: string;
    waTracking?: string;
    errorTextColor?: string;
    leadInfo?: { advertiseSource: string; leadStatus?: string };
    leadTrackingAction?: { page: { action: string } };
    qaInput?: string;
    qaButton?: string;
    inputClasses?: string;
    buttonClasses?: string;
    beforeSubmit?: () => void;
    onSuccess?: () => void;
    onError?: () => void;
    useBirthdate?: boolean;
    ariaLabel?: string;
    buttonStyletype?: 'primary' | 'secondary' | 'reskin' | 'orange' | 'link' | 'rebrandAction';
    formPlaceholder?: string;
    disabled?: boolean;
    emailLabelText?: string;
    emailLabelTextColor?: string;
    emailFormMarginTop?: string;
    isLabelHidden?: boolean;
};

const EmailLeadForm = ({
    ariaLabel,
    beforeSubmit = () => undefined,
    buttonClasses = '',
    buttonStyletype,
    buttonText = 'Get The Guide',
    className = '',
    disabled = false,
    emailFormMarginTop,
    emailLabelText,
    emailLabelTextColor,
    errorTextColor = 'text-red',
    formPlaceholder = '',
    inputClasses = '',
    isLabelHidden,
    isMobileSideBySide = false,
    isStacked = false,
    leadInfo = { advertiseSource: 'NTMGuide', leadStatus: 'NTMGuide' },
    leadTrackingAction,
    mktoFormId = 1004,
    onError = () => undefined,
    onSuccess = () => undefined,
    qaButton = 'get-the-guide',
    qaInput = 'email-textbox',
    uid,
    useBirthdate = false,
    waTracking = '',
}: Props) => {
    // mktoFormId = process.env.GATSBY_SUBDOMAIN ? 1004 : mktoFormId;
    const dispatch = useAppDispatch();
    const [id] = useState(() => uid || `email-${Math.random().toString(36).substring(2, 9)}`);
    const [emailValue, setEmailValue] = useState('');
    const [dateValue, setDateValue] = useState('');
    const [isEmailValid, setIsEmailValid] = useState(true);
    const [isDateValid, setIsDateValid] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const mktoForm = new MarketoForm(mktoFormId);
    const allid = useAppSelector((state) => state.global.allianceId);
    const gclid = useAppSelector((state) => state.global.gclid);
    const fbclid = useAppSelector((state) => state.global.fbclid);
    const msclkid = useAppSelector((state) => state.global.msclid);
    const { isMobile } = useWindowSize();

    const [version, setVersion] = useState('a');

    const findTestVersion = (attempts = 0) => {
        setTimeout(() => {
            if (attempts > 20) {
                return;
            }
            if (typeof window !== 'undefined' && window.ehimTest) {
                setVersion(window.ehimTest);
            } else {
                findTestVersion(attempts + 1);
            }
        }, 200);
    };

    useEffect(() => {
        findTestVersion();
    }, []);

    useEffect(() => {
        setTimeout(() => mktoForm.init(), 1000);
    }, []);

    const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
        setIsEmailValid(true);
        setEmailValue(e.target.value);
    };

    const handleDateChange = (value: string) => {
        setIsDateValid(true);
        setDateValue(value);
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
        e.preventDefault();
        beforeSubmit();

        if (isLoading) return;
        setIsLoading(true);

        if (!_validateForm()) {
            setIsLoading(false);
            return;
        }

        mktoForm.mktoFormSubmit(emailValue, allid);

        const leadRequest: LeadRequestArgs = {
            email: emailValue,
            emailStatus: 'Active',
            allianceId: allid,
            productType: 'MC',
            leadType: 'PP',
            adobeMid: window._satellite?.getVar?.('Adobe MCID') || '',
            fullEhivisitorId: window._satellite?.getVar?.('Adobe MCID') || '',
        };

        if (useBirthdate) {
            leadRequest.leadMember = { birthDate: dateValue };
        }

        const advertise = getAdvertiseId(gclid, fbclid, msclkid) as { [key: string]: string };
        if (advertise) {
            leadRequest.advertiseIdType = advertise.id.toUpperCase();
            leadRequest.advertiseId = advertise[advertise.id];
        }

        const leadData = Object.assign(leadRequest, leadInfo);

        try {
            const lead = (
                await createLead(leadData, {
                    disclaimerText:
                        'Your information and use of this site is governed by our most recent Terms of Use and Privacy Policy. You consent to receive marketing emails from eHealth.',
                    disclaimerUrl: window.location.href,
                    visitTime: new Date().toJSON(),
                    userAgent: window.navigator?.userAgent || '',
                })
            )?.data.searchCreateLead;
            if (lead?.success) {
                dispatch(updateLead(String(lead.leadId), lead.leadToken));
                onSuccess();
                fireTrackEvent({
                    leadInfo: {
                        leadId: lead.leadId,
                        email: emailValue,
                        requestedDocument: leadInfo?.advertiseSource,
                    },
                    eventList: ['leadCreated', 'save_results'],
                    ...leadTrackingAction,
                });
            } else {
                throw lead?.message;
            }
        } catch (err) {
            console.error('Lead error:', err);
            onError();
        } finally {
            setIsLoading(false);
        }
    };

    const _validateForm = () => {
        let firstError = null;

        if (!_validateEmail()) {
            firstError = `email_${id}`;
        }
        if (!_validateBirthdate()) {
            firstError = firstError || `birthdate_${id}`;
        }
        if (firstError) {
            document.getElementById(firstError)?.focus();
            return false;
        }
        return true;
    };

    const _validateEmail = () => {
        if (emailValue) {
            if (isValidEmail(emailValue)) {
                setIsEmailValid(true);
                return true;
            } else {
                fireTrackEvent({
                    form: {
                        errors: 'invalid email address',
                    },
                });
            }
        }
        setIsEmailValid(false);
        return false;
    };

    const _validateBirthdate = () => {
        if (!useBirthdate) return true;
        if (dateValue) {
            const date = new Date(dateValue);
            const year = date.getFullYear();
            const currentDate = new Date();
            const currentYear = currentDate.getFullYear();

            if (year >= currentYear - 120 && date <= currentDate) {
                setIsDateValid(true);
                return true;
            } else {
                fireTrackEvent({
                    form: {
                        errors: 'Invalid date',
                    },
                });
            }
        }
        setIsDateValid(false);
        return false;
    };

    return (
        <form
            noValidate
            className={className}
            onSubmit={(e) => {
                handleSubmit(e).catch((err) => {
                    throw err;
                });
            }}
        >
            <p role="alert" className="sr-only">
                {!isEmailValid && 'Invalid email address.'}
                {!isDateValid && 'Invalid date.'}
            </p>
            <label
                htmlFor={id}
                className={classNames(
                    'font-poppins text-14 leading-20 mb-1',
                    `text-${emailLabelTextColor || '[#F0F0F0]'}`,
                    isLabelHidden && 'sr-only'
                )}
            >
                {emailLabelText || 'Enter your email'}
            </label>
            <div
                className={classNames(
                    'grid items-start gap-y-4',
                    !isStacked && 'gap-x-4 md:grid-cols-4',
                    isMobileSideBySide && 'gap-x-4 grid-cols-4 md:grid-cols-2'
                )}
            >
                <div
                    className={classNames(
                        inputClasses,
                        (!isStacked || isMobileSideBySide) && 'col-span-2'
                    )}
                >
                    <input
                        className={classNames(
                            'border rounded placeholder-gray text-black w-full font-poppins',
                            isEmailValid
                                ? 'border-gray'
                                : 'border-red bg-red-lightest border-2 mb-1',
                            isMobile && isMobileSideBySide ? 'p-2 leading-5' : 'p-3',
                            `mt-${emailFormMarginTop || '0'}`
                        )}
                        id={id}
                        autoComplete="email"
                        onChange={handleEmailChange}
                        type="text"
                        value={emailValue}
                        aria-invalid={!isEmailValid}
                        data-qa={qaInput}
                        aria-label={ariaLabel}
                        placeholder={isMobileSideBySide ? 'Enter Email' : formPlaceholder}
                        disabled={disabled}
                    />
                    <div
                        className={isEmailValid ? 'hidden' : 'w-full flex items-center'}
                        data-qa="error-message"
                    >
                        <img
                            src={errorIcon}
                            alt=""
                            aria-hidden
                            width={16}
                            className="mr-1"
                            data-qa="error-message-icon"
                        />
                        <span
                            className={`text-sm font-poppins ${errorTextColor}`}
                            data-qa="error-message-text"
                            aria-live="polite"
                        >
                            Invalid email address
                        </span>
                    </div>
                </div>
                {useBirthdate && (
                    <div className="mb-2">
                        <label htmlFor={`birthdate_${id}`}>Birthdate</label>
                        <ReactInputDateMask
                            id={`birthdate_${id}`}
                            autoComplete="bday"
                            mask="mm/dd/yyyy"
                            value={dateValue}
                            onChange={handleDateChange}
                            ariaInvalid={!isDateValid}
                            className={classNames(
                                'pl-3 w-full h-12 rounded',
                                isDateValid
                                    ? 'border border-gray bg-white'
                                    : 'border-2 border-red bg-red-lightest mb-1'
                            )}
                            disabled={disabled}
                        />
                        <div
                            className={isDateValid ? 'hidden' : 'w-full flex items-center'}
                            data-qa="error-message"
                        >
                            <img
                                src={errorIcon}
                                alt=""
                                aria-hidden
                                width={16}
                                className="mr-1"
                                data-qa="error-message-icon"
                            />
                            <span
                                className={`text-sm ${errorTextColor}`}
                                data-qa="error-message-text"
                            >
                                Invalid date
                            </span>
                        </div>
                    </div>
                )}
                <Button
                    type="submit"
                    data-wa-link={waTracking}
                    data-qa={qaButton}
                    className={classNames(
                        buttonClasses,
                        (!isStacked || isMobileSideBySide) && 'col-span-2',
                        isMobile && isMobileSideBySide ? '!p-2 !h-auto' : ''
                    )}
                    styletype={
                        buttonStyletype !== undefined
                            ? buttonStyletype
                            : version !== 'b'
                              ? 'primary'
                              : 'orange'
                    }
                    disabled={disabled}
                >
                    <span className="font-poppins">
                        {isLoading ? (
                            <Spinner color="white" />
                        ) : (isMobile && isMobileSideBySide && buttonText) === 'Get The Guide' ? (
                            'Get Guide'
                        ) : (
                            buttonText
                        )}
                    </span>
                </Button>
            </div>
        </form>
    );
};

export default EmailLeadForm;
